import { useState, useMemo } from 'react';
import { categoriesAliments } from '@/mocks/aliments';
import { useAliments } from '@/hooks/useAliments';
import { Aliment } from '@/mocks/aliments';

export interface RepasItem {
  aliment: Aliment;
  quantite: number;
}

interface AlimentSelectorProps {
  repas: RepasItem[];
  onRepasChange: (repas: RepasItem[]) => void;
}

const categoryColors: Record<string, string> = {
  legumineuses: 'bg-lime-100 text-lime-700',
  feculents: 'bg-amber-100 text-amber-700',
  legumes: 'bg-emerald-100 text-emerald-700',
  fruits: 'bg-orange-100 text-orange-700',
  viandes: 'bg-rose-100 text-rose-700',
  poissons: 'bg-cyan-100 text-cyan-700',
  produits_laitiers: 'bg-sky-100 text-sky-700',
  boissons: 'bg-blue-100 text-blue-700',
  snacks: 'bg-yellow-100 text-yellow-700',
  desserts: 'bg-pink-100 text-pink-700',
  fast_food: 'bg-red-100 text-red-700',
  sauces: 'bg-stone-100 text-stone-700',
  petit_dejeuner: 'bg-amber-100 text-amber-800',
  produits_industriels: 'bg-violet-100 text-violet-700',
};

export default function AlimentSelector({ repas, onRepasChange }: AlimentSelectorProps) {
  const [activeCategory, setActiveCategory] = useState('tous');
  const [search, setSearch] = useState('');

  const { allAliments } = useAliments();

  const filtered = useMemo(() => {
    let list = allAliments;
    if (activeCategory !== 'tous') list = list.filter((a) => a.categorie === activeCategory);
    if (search.trim()) list = list.filter((a) => a.nom.toLowerCase().includes(search.toLowerCase()));
    return list;
  }, [allAliments, activeCategory, search]);

  const isInRepas = (id: number) => repas.some((r) => r.aliment.id === id);

  const addToRepas = (aliment: Aliment) => {
    if (isInRepas(aliment.id)) return;
    onRepasChange([...repas, { aliment, quantite: 100 }]);
  };

  const removeFromRepas = (id: number) => {
    onRepasChange(repas.filter((r) => r.aliment.id !== id));
  };

  return (
    <div>
      {/* Search */}
      <div className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 mb-4 focus-within:border-teal-400 transition-colors">
        <svg className="w-4 h-4 text-gray-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8"/>
          <path d="m21 21-4.3-4.3"/>
        </svg>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher un aliment..."
          className="flex-1 outline-none text-sm text-gray-800 placeholder-gray-400 bg-transparent"
        />
        {search && (
          <button onClick={() => setSearch('')} className="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6 6 18"/>
              <path d="m6 6 12 12"/>
            </svg>
          </button>
        )}
      </div>

      {/* Category filters */}
      <div className="flex flex-wrap gap-2 mb-5">
        <button
          onClick={() => setActiveCategory('tous')}
          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${activeCategory === 'tous' ? 'bg-teal-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-teal-50 hover:text-teal-600'}`}
        >
          Tous ({allAliments.length})
        </button>
        {categoriesAliments.map((cat) => {
          const count = allAliments.filter((a) => a.categorie === cat.id).length;
          return (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${activeCategory === cat.id ? 'bg-teal-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-teal-50 hover:text-teal-600'}`}
            >
              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 16v-4"/>
                <path d="M12 8h.01"/>
              </svg>
              {cat.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Aliments grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {filtered.map((aliment) => {
          const inRepas = isInRepas(aliment.id);
          const catLabel = categoriesAliments.find((c) => c.id === aliment.categorie)?.label ?? '';
          return (
            <div
              key={aliment.id}
              className={`relative rounded-xl overflow-hidden border-2 transition-all duration-200 ${inRepas ? 'border-teal-400 bg-teal-50/30' : 'border-gray-100 bg-white hover:border-teal-200'}`}
            >
              {/* Image */}
              <div className="relative w-full h-28 overflow-hidden">
                <img
                  src={aliment.image}
                  alt={aliment.nom}
                  className="w-full h-full object-cover object-top"
                />
                <span className={`absolute top-2 left-2 text-xs font-semibold px-1.5 py-0.5 rounded-full ${categoryColors[aliment.categorie] ?? 'bg-gray-100 text-gray-600'}`}>
                  {catLabel}
                </span>
              </div>

              {/* Info */}
              <div className="p-2.5 pb-8">
                <p className="text-xs font-semibold text-gray-800 leading-tight">{aliment.nom}</p>
                <p className="text-xs text-teal-600 mt-0.5 font-medium">{aliment.glucidesPour100g}g G/100g</p>
              </div>

              {/* Add / Remove button */}
              {inRepas ? (
                <button
                  onClick={() => removeFromRepas(aliment.id)}
                  className="absolute bottom-2 right-2 w-7 h-7 flex items-center justify-center bg-red-100 hover:bg-red-200 text-red-500 rounded-full transition-all cursor-pointer"
                  title="Retirer du repas"
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14"/>
                  </svg>
                </button>
              ) : (
                <button
                  onClick={() => addToRepas(aliment)}
                  className="absolute bottom-2 right-2 w-7 h-7 flex items-center justify-center bg-teal-500 hover:bg-teal-400 text-white rounded-full transition-all cursor-pointer"
                  title="Ajouter au repas"
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14"/>
                    <path d="M12 5v14"/>
                  </svg>
                </button>
              )}
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/>
            <path d="m21 21-4.3-4.3"/>
          </svg>
          <p className="text-sm">Aucun aliment trouvé</p>
        </div>
      )}
    </div>
  );
}
